import { Button, Typography } from "@trustpilot/businessapp-patternlibrary";
import { TimeAgo } from "ilenia";

import { ShellSecondaryDataResponseBody } from "../../../types";
import { keyboardEnterOrSpaceClick } from "../../sidebar/utils/accessiblity";
import {
  StyledItem,
  StyledItemHeader,
  StyledItemDescription,
  StyledItemTimeago,
  StyledItemCta,
  StyledItemHeaderTypography,
  StyledItemDescriptionUrlWrapper,
} from "./styled";

type Match = {
  fullMatch: string;
  tag: string;
  content: string;
  index: number;
};

// Parses custom tags in the notification description:
// - [LINK]url[LINK][LINK_BUTTON]text[LINK_BUTTON] for links/buttons
// - [ITALICS]text[ITALICS] for italics (renders as <em>)
// Links can be ended with _self to modify the default target, which is _blank by default.
const processTags = (
  description: string,
  trackNotificationItemUrlClicked: (id: string, title: string, target: string) => Promise<void>,
  id: string,
  title: string,
) => {
  const tagRegex = /\[(\w+)\](.*?)\[\1\]/g;

  const matches: Match[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tagRegex.exec(description)) !== null) {
    matches.push({
      fullMatch: match[0],
      tag: match[1],
      content: match[2],
      index: match.index,
    });
  }

  const result = [];

  // Use i++ because some tags (like [ITALICS]) are single, while others (like [LINK]... [LINK_BUTTON]) are handled in pairs.
  // For link pairs, we increment i again inside the link handling block.
  for (let i = 0; i < matches.length; i++) {
    const tagContent = matches[i];

    // Add any text before the current tag
    if (lastIndex < tagContent.index) {
      result.push(description.slice(lastIndex, tagContent.index));
    }

    // Handle ITALICS tag
    if (tagContent.tag.toUpperCase() === "ITALICS") {
      result.push(<em key={`italics-${i}`}>{tagContent.content}</em>);
      lastIndex = tagContent.index + tagContent.fullMatch.length;
      continue;
    }

    // Handle LINK tags
    if (
      tagContent.tag.toUpperCase().startsWith("LINK") &&
      i + 1 < matches.length &&
      matches[i + 1].tag.toUpperCase().startsWith("LINK")
    ) {
      const urlTagContent = tagContent;
      const textTagContent = matches[i + 1];
      //open tag in new page by default
      let target = "_blank";
      if (urlTagContent.tag.toLowerCase().endsWith("_self")) {
        target = "_self";
      }
      result.push(
        <StyledItemDescriptionUrlWrapper
          key={`link-${i}`}
          href={urlTagContent.content}
          target={target}
          onClick={(e) => {
            e.stopPropagation();
            trackNotificationItemUrlClicked(id, title, urlTagContent.content);
          }}
        >
          {textTagContent.content}
        </StyledItemDescriptionUrlWrapper>,
      );

      lastIndex = textTagContent.index + textTagContent.fullMatch.length;
      i++; // Skip the next match since it's already handled as part of the pair
      continue;
    }

    // Fallback: just output the tag as plain text
    result.push(tagContent.fullMatch);
    lastIndex = tagContent.index + tagContent.fullMatch.length;
  }

  // Add any remaining text after the last tag
  if (lastIndex < description.length) {
    result.push(description.slice(lastIndex));
  }

  return result;
};

type Notification = ShellSecondaryDataResponseBody["notifications"][number];
interface INotificationItem extends Notification {
  onItemClicked: (
    event: React.MouseEvent | React.KeyboardEvent,
    id: string,
    action: { type?: string; descriptor?: string; path?: string },
    read: boolean,
    type: string,
  ) => void;
  trackNotificationItemUrlClicked: (id: string, title: string, target: string) => Promise<void>;
}

const NotificationItem: React.FC<INotificationItem> = ({
  state,
  id,
  createdAt,
  action,
  title,
  description,
  cta,
  type,
  onItemClicked,
  trackNotificationItemUrlClicked,
}) => {
  return (
    <StyledItem key={id} read={state !== "new"}>
      <div
        role="button"
        tabIndex={0}
        onClick={(event) => onItemClicked(event, id, action, state !== "new", type)}
        onKeyDown={(event) => {
          keyboardEnterOrSpaceClick(event, () => onItemClicked(event, id, action, state !== "new", type));
        }}
      >
        <StyledItemHeader>
          <StyledItemHeaderTypography variant="heading-s">{title}</StyledItemHeaderTypography>
        </StyledItemHeader>
        <StyledItemTimeago>
          <TimeAgo date={createdAt} />
        </StyledItemTimeago>
        <StyledItemDescription>
          <Typography variant="body-m">
            {processTags(description, trackNotificationItemUrlClicked, id, title)}
          </Typography>
        </StyledItemDescription>
        {action.path && cta && (
          <StyledItemCta>
            <Button size="small">{cta}</Button>
          </StyledItemCta>
        )}
      </div>
    </StyledItem>
  );
};

export default NotificationItem;
